import { useEffect, useState, useMemo } from 'react';
import { AlertTriangle, Settings, Flag, DownloadCloud, CreditCard, Info, LockKeyhole } from 'lucide-react';
import { toast } from 'sonner';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  createMobileConfigUpdate,
  fetchSystemSettings,
  mergeRemoteConfig,
  mergeSubscriptionConfig,
  updateSystemSettings,
} from '@/features/admin';
import type { RemoteConfig, MaintenanceConfig, ForceUpdateConfig, MobileAppConfig } from '@/features/admin';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

import { SubscriptionConfigTab } from '@/components/admin/SubscriptionConfigTab';
import type { SubscriptionConfig } from '@/features/admin';

type RemoteConfigTab = 'general' | 'maintenance' | 'featureFlags' | 'subscriptions';

export default function MobileRemoteConfigPage() {
  const dispatch = useAppDispatch();

  const settingsMap = useAppSelector((state) => state.settings.settings as MobileAppConfig | null);
  const isLoading = useAppSelector((state) => state.settings.isLoading);
  const isSaving = useAppSelector((state) => state.settings.isSaving);
  const hasFetched = useAppSelector((state) => state.settings.hasFetched);
  const hydratedFromDefaults = useAppSelector((state) => state.settings.hydratedFromDefaults);

  const remoteConfigFromStore = settingsMap?.REMOTE_CONFIG;
  const subscriptionFromStore = settingsMap?.SUBSCRIPTIONS_CONFIG;

  const effectiveConfig: RemoteConfig = useMemo(
    () => mergeRemoteConfig(remoteConfigFromStore),
    [remoteConfigFromStore],
  );

  const effectiveSubscriptionConfig = useMemo(
    () => mergeSubscriptionConfig(subscriptionFromStore),
    [subscriptionFromStore],
  );

  const [localConfig, setLocalConfig] = useState<RemoteConfig>(effectiveConfig);
  const [localSubscriptionConfig, setLocalSubscriptionConfig] =
    useState<SubscriptionConfig>(effectiveSubscriptionConfig);
  const [hasChanges, setHasChanges] = useState(false);
  const [activeTab, setActiveTab] = useState<RemoteConfigTab>('general');
  const canSave = hasChanges || hydratedFromDefaults;

  useEffect(() => {
    if (!hasFetched) {
      dispatch(fetchSystemSettings());
    }
  }, [dispatch, hasFetched]);

  useEffect(() => {
    setLocalConfig(effectiveConfig);
    setLocalSubscriptionConfig(effectiveSubscriptionConfig);
    setHasChanges(false);
  }, [effectiveConfig, effectiveSubscriptionConfig]);

  useEffect(() => {
    if (!hasFetched) {
      setHasChanges(false);
      return;
    }

    const remoteChanged = JSON.stringify(localConfig) !== JSON.stringify(effectiveConfig);
    const subscriptionsChanged =
      JSON.stringify(localSubscriptionConfig) !== JSON.stringify(effectiveSubscriptionConfig);

    setHasChanges(remoteChanged || subscriptionsChanged);
  }, [localConfig, localSubscriptionConfig, effectiveConfig, effectiveSubscriptionConfig, hasFetched]);

  // ---- Helpers to update nested pieces ----
  const updateMaintenance = (patch: Partial<MaintenanceConfig>) => {
    setLocalConfig((prev) => ({
      ...prev,
      maintenance: { ...prev.maintenance, ...patch },
    }));
  };

  const updateSubscriptionConfig = (subscriptionConfig: SubscriptionConfig) => {
    setLocalSubscriptionConfig(subscriptionConfig);
  };

  const updateGeneral = (patch: Partial<RemoteConfig>) => {
    setLocalConfig((prev) => ({
      ...prev,
      documentMaxSize: patch.documentMaxSize ?? prev.documentMaxSize,
      defaultMaxOTPAttempts: patch.defaultMaxOTPAttempts ?? prev.defaultMaxOTPAttempts,
      imagePickerConfig: patch.imagePickerConfig
        ? { ...prev.imagePickerConfig, ...patch.imagePickerConfig }
        : prev.imagePickerConfig,
      imageTtl: patch.imageTtl ?? prev.imageTtl,
      defaultRequestTimeout: patch.defaultRequestTimeout ?? prev.defaultRequestTimeout,
    }));
  };

  const updateForceUpdate = (patch: Partial<ForceUpdateConfig>) => {
    setLocalConfig((prev) => ({
      ...prev,
      forceUpdate: {
        ...prev.forceUpdate,
        ...patch,
        storeUrl: {
          ...prev.forceUpdate.storeUrl,
          ...patch.storeUrl,
        },
      },
    }));
  };

  const updateFeatureFlagKey = (oldKey: string, newKey: string) => {
    setLocalConfig((prev) => {
      const flags = { ...prev.featureFlags };
      const value = flags[oldKey];
      delete flags[oldKey];
      if (newKey.trim()) {
        flags[newKey.trim()] = value ?? false;
      }
      return { ...prev, featureFlags: flags };
    });
  };

  const updateFeatureFlagValue = (key: string, value: boolean) => {
    setLocalConfig((prev) => ({
      ...prev,
      featureFlags: { ...prev.featureFlags, [key]: value },
    }));
  };

  const addFeatureFlag = () => {
    setLocalConfig((prev) => {
      const flags = { ...prev.featureFlags };
      let index = 1;
      let key = `newFeature${index}`;
      while (flags[key] !== undefined) {
        index += 1;
        key = `newFeature${index}`;
      }
      flags[key] = false;
      return { ...prev, featureFlags: flags };
    });
  };

  const removeFeatureFlag = (key: string) => {
    setLocalConfig((prev) => {
      const flags = { ...prev.featureFlags };
      delete flags[key];
      return { ...prev, featureFlags: flags };
    });
  };

  // ---- Save / Cancel ----
  const handleSave = async () => {
    // Persist operational settings only. Client routing is immutable build/deployment config,
    // and subscriptions have one top-level source of truth.
    const promise = dispatch(
      updateSystemSettings(createMobileConfigUpdate(localConfig, localSubscriptionConfig)),
    ).unwrap();

    toast.promise(promise, {
      loading: 'Saving mobile app config...',
      success: 'Mobile app config updated successfully',
      error: (err) => err?.message || 'Failed to update mobile app config',
    });

    try {
      await promise;
      dispatch(fetchSystemSettings());
    } catch {
      // toast already handled
    }
  };

  const handleCancel = () => {
    setLocalConfig(effectiveConfig);
    setLocalSubscriptionConfig(effectiveSubscriptionConfig);
  };

  if (isLoading && !hasFetched) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <LoadingSpinner size={80} />
      </div>
    );
  }

  return (
    <div className="container mx-auto space-y-6 py-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Mobile App Operational Configuration</h1>
          <div className="text-muted-foreground">
            <span className="font-medium">Version:</span> {remoteConfigFromStore?.configVersion ?? 0}
          </div>
          <div className="text-muted-foreground">
            <span className="font-medium">Last Updated:</span>{' '}
            {remoteConfigFromStore?.lastUpdated
              ? new Date(remoteConfigFromStore.lastUpdated * 1000).toLocaleString()
              : '—'}
          </div>
        </div>

        <div className="flex gap-3">
          <Button variant="outline" onClick={handleCancel} disabled={!hasChanges || isSaving}>
            Reset
          </Button>
          <Button onClick={handleSave} disabled={!canSave || isSaving}>
            {isSaving ? (
              <>
                <LoadingSpinner size={16} className="mr-2" />
                Saving...
              </>
            ) : (
              'Save All Changes'
            )}
          </Button>
        </div>
      </div>

      {hydratedFromDefaults ? (
        <div className="border-border bg-muted/50 text-foreground flex items-start gap-3 rounded-md border px-4 py-3">
          <Info className="mt-0.5 h-4 w-4 shrink-0" />
          <div className="space-y-1">
            <p className="text-sm font-medium">No remote config found, defaults hydrated</p>
            <p className="text-muted-foreground text-sm">
              Save all changes to create the remote mobile app config from these defaults.
            </p>
          </div>
        </div>
      ) : null}

      <div className="border-border bg-muted/50 text-foreground flex items-start gap-3 rounded-md border px-4 py-3">
        <LockKeyhole className="mt-0.5 h-4 w-4 shrink-0" />
        <div className="space-y-1">
          <p className="text-sm font-medium">API and authentication routing is build-controlled</p>
          <p className="text-muted-foreground text-sm">
            Mobile URLs are compiled into each native build, and web URLs come from its deployment config. This page
            controls operational behavior only; it cannot redirect clients between environments.
          </p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as RemoteConfigTab)} className="space-y-6">
        {/* Segmented/tab header */}
        <div className="bg-background/80 sticky top-0 z-10 -mx-2 px-2 py-2 backdrop-blur">
          <TabsList className="flex h-auto w-full flex-wrap justify-start gap-1">
            <TabsTrigger value="general" className="gap-2">
              <Settings className="h-4 w-4" />
              General
            </TabsTrigger>
            <TabsTrigger value="maintenance" className="gap-2">
              <AlertTriangle className="h-4 w-4" />
              Maintenance
            </TabsTrigger>
            <TabsTrigger value="featureFlags" className="gap-2">
              <Flag className="h-4 w-4" />
              Feature Flags
            </TabsTrigger>

            <TabsTrigger value="subscriptions" className="gap-2">
              <CreditCard className="h-4 w-4" />
              Subscriptions
            </TabsTrigger>
          </TabsList>
        </div>

        {/* Overview (Metadata) */}
        <TabsContent value="general" className="space-y-6">
          <div className="grid gap-6 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="h-5 w-5" />
                  App Settings
                </CardTitle>
                <CardDescription>General performance and security constraints.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="documentMaxSize">Document Max Size (Bytes)</Label>
                  <Input
                    id="documentMaxSize"
                    type="number"
                    min={1}
                    step={1}
                    value={localConfig.documentMaxSize ?? 0}
                    onChange={(e) => updateGeneral({ documentMaxSize: Number(e.target.value) })}
                    disabled={isSaving}
                  />
                  <p className="text-muted-foreground text-xs">
                    Current: {((localConfig.documentMaxSize ?? 0) / (1024 * 1024)).toFixed(2)} MB
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="defaultMaxOTPAttempts">Default Max OTP Attempts</Label>
                  <Input
                    id="defaultMaxOTPAttempts"
                    type="number"
                    min={1}
                    max={10}
                    step={1}
                    value={localConfig.defaultMaxOTPAttempts ?? 0}
                    onChange={(e) => updateGeneral({ defaultMaxOTPAttempts: Number(e.target.value) })}
                    disabled={isSaving}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="imageTtl">Image TTL (Seconds)</Label>
                  <Input
                    id="imageTtl"
                    type="number"
                    min={0}
                    step={1}
                    value={localConfig.imageTtl ?? 0}
                    onChange={(e) => updateGeneral({ imageTtl: Number(e.target.value) })}
                    disabled={isSaving}
                  />
                  <p className="text-muted-foreground text-xs">
                    Approx. {((localConfig.imageTtl ?? 0) / 60).toFixed(1)} minutes
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="defaultRequestTimeout">Default Request Timeout (ms)</Label>
                  <Input
                    id="defaultRequestTimeout"
                    type="number"
                    min={1}
                    step={1}
                    value={localConfig.defaultRequestTimeout ?? 0}
                    onChange={(e) => updateGeneral({ defaultRequestTimeout: Number(e.target.value) })}
                    disabled={isSaving}
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DownloadCloud className="h-5 w-5" />
                  Image Picker Config
                </CardTitle>
                <CardDescription>Compression and dimension limits for uploaded images.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="img-quality">Quality (0.0 - 1.0)</Label>
                  <Input
                    id="img-quality"
                    type="number"
                    step="0.1"
                    min="0"
                    max="1"
                    value={localConfig.imagePickerConfig?.quality ?? 0.5}
                    onChange={(e) =>
                      updateGeneral({
                        imagePickerConfig: {
                          ...localConfig.imagePickerConfig!,
                          quality: Number(e.target.value) as RemoteConfig['imagePickerConfig']['quality'],
                        },
                      })
                    }
                    disabled={isSaving}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="img-maxWidth">Max Width (px)</Label>
                    <Input
                      id="img-maxWidth"
                      type="number"
                      min={1}
                      step={1}
                      value={localConfig.imagePickerConfig?.maxWidth ?? 800}
                      onChange={(e) =>
                        updateGeneral({
                          imagePickerConfig: {
                            ...localConfig.imagePickerConfig!,
                            maxWidth: Number(e.target.value),
                          },
                        })
                      }
                      disabled={isSaving}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="img-maxHeight">Max Height (px)</Label>
                    <Input
                      id="img-maxHeight"
                      type="number"
                      min={1}
                      step={1}
                      value={localConfig.imagePickerConfig?.maxHeight ?? 600}
                      onChange={(e) =>
                        updateGeneral({
                          imagePickerConfig: {
                            ...localConfig.imagePickerConfig!,
                            maxHeight: Number(e.target.value),
                          },
                        })
                      }
                      disabled={isSaving}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Maintenance Mode */}
        <TabsContent value="maintenance" className="space-y-6">
          <Card className="max-w-3xl">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-500" />
                Maintenance Mode
              </CardTitle>
              <CardDescription>Controls global maintenance screen shown to all clients.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <Label htmlFor="maintenance-enabled" className="text-base font-medium">
                    Enable Maintenance Mode
                  </Label>
                  <p className="text-muted-foreground text-sm">
                    When enabled, clients should block normal usage and show the maintenance UI.
                  </p>
                </div>
                <Switch
                  id="maintenance-enabled"
                  checked={localConfig.maintenance.enabled}
                  onCheckedChange={(v) => updateMaintenance({ enabled: v })}
                  disabled={isSaving}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="maintenance-title">Title</Label>
                <Input
                  id="maintenance-title"
                  value={localConfig.maintenance.title ?? ''}
                  onChange={(e) => updateMaintenance({ title: e.target.value })}
                  disabled={!localConfig.maintenance.enabled || isSaving}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="maintenance-message">Message</Label>
                <Textarea
                  id="maintenance-message"
                  className="min-h-[100px]"
                  placeholder="We are upgrading our systems. Please check back later."
                  value={localConfig.maintenance.message ?? ''}
                  onChange={(e) => updateMaintenance({ message: e.target.value })}
                  disabled={!localConfig.maintenance.enabled || isSaving}
                />
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="maintenance-end">Estimated End Time (optional)</Label>
                  <Input
                    id="maintenance-end"
                    type="datetime-local"
                    value={localConfig.maintenance.estimatedEndTime ?? ''}
                    onChange={(e) => updateMaintenance({ estimatedEndTime: e.target.value || undefined })}
                    disabled={!localConfig.maintenance.enabled || isSaving}
                  />
                  <p className="text-muted-foreground text-xs">
                    Clients may display this to set expectations for users.
                  </p>
                </div>
                <div className="flex items-center justify-between rounded-md border p-3">
                  <div className="space-y-1">
                    <Label htmlFor="maintenance-retry">Allow Retry Button</Label>
                    <p className="text-muted-foreground text-xs">
                      Shows a &quot;Try again&quot; button so clients can re-fetch the config.
                    </p>
                  </div>
                  <Switch
                    id="maintenance-retry"
                    checked={localConfig.maintenance.allowRetry}
                    onCheckedChange={(v) => updateMaintenance({ allowRetry: v })}
                    disabled={!localConfig.maintenance.enabled || isSaving}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="max-w-3xl">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DownloadCloud className="h-5 w-5" />
                Force Update
              </CardTitle>
              <CardDescription>Enforce a minimum mobile app version and direct users to app stores.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <Label htmlFor="force-enabled">Enable Force Update</Label>
                  <p className="text-muted-foreground text-sm">
                    When enabled, clients below the minimum version should block usage and show this message.
                  </p>
                </div>
                <Switch
                  id="force-enabled"
                  checked={localConfig.forceUpdate.enabled}
                  onCheckedChange={(v) => updateForceUpdate({ enabled: v })}
                  disabled={isSaving}
                />
              </div>

              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2 md:col-span-1">
                  <Label htmlFor="force-minVersion">Minimum Version</Label>
                  <Input
                    id="force-minVersion"
                    placeholder="1.2.0"
                    value={localConfig.forceUpdate.minVersion}
                    onChange={(e) => updateForceUpdate({ minVersion: e.target.value })}
                    disabled={!localConfig.forceUpdate.enabled || isSaving}
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="force-title">Title</Label>
                  <Input
                    id="force-title"
                    value={localConfig.forceUpdate.title ?? ''}
                    onChange={(e) => updateForceUpdate({ title: e.target.value })}
                    disabled={!localConfig.forceUpdate.enabled || isSaving}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="force-message">Message</Label>
                <Textarea
                  id="force-message"
                  className="min-h-[80px]"
                  placeholder="A new version of HomeVein is available. Please update to continue."
                  value={localConfig.forceUpdate.message}
                  onChange={(e) => updateForceUpdate({ message: e.target.value })}
                  disabled={!localConfig.forceUpdate.enabled || isSaving}
                />
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="force-ios">iOS Store URL</Label>
                  <Input
                    id="force-ios"
                    placeholder="https://apps.apple.com/..."
                    value={localConfig.forceUpdate.storeUrl?.ios ?? ''}
                    onChange={(e) =>
                      updateForceUpdate({
                        storeUrl: {
                          ...localConfig.forceUpdate.storeUrl,
                          ios: e.target.value,
                        },
                      })
                    }
                    disabled={!localConfig.forceUpdate.enabled || isSaving}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="force-android">Android Store URL</Label>
                  <Input
                    id="force-android"
                    placeholder="https://play.google.com/store/apps/..."
                    value={localConfig.forceUpdate.storeUrl?.android ?? ''}
                    onChange={(e) =>
                      updateForceUpdate({
                        storeUrl: {
                          ...localConfig.forceUpdate.storeUrl,
                          android: e.target.value,
                        },
                      })
                    }
                    disabled={!localConfig.forceUpdate.enabled || isSaving}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Feature Flags */}
        <TabsContent value="featureFlags" className="space-y-6">
          <Card className="max-w-4xl">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Flag className="h-5 w-5" />
                Feature Flags
              </CardTitle>
              <CardDescription>Toggle experimental features per key.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {Object.entries(localConfig.featureFlags).map(([key, value]) => (
                <div key={key} className="flex flex-wrap items-center gap-3">
                  <Input
                    className="w-56"
                    value={key}
                    onChange={(e) => updateFeatureFlagKey(key, e.target.value)}
                    placeholder="featureKey"
                    disabled={isSaving}
                  />
                  <Switch
                    checked={!!value}
                    onCheckedChange={(v) => updateFeatureFlagValue(key, v)}
                    disabled={isSaving}
                  />
                  <span className="text-muted-foreground text-xs">{value ? 'Enabled' : 'Disabled'}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="ml-auto text-xs text-red-500"
                    onClick={() => removeFeatureFlag(key)}
                    disabled={isSaving}
                  >
                    Remove
                  </Button>
                </div>
              ))}

              <Button variant="outline" size="sm" onClick={addFeatureFlag} disabled={isSaving}>
                + Add Feature Flag
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Subscriptions Tab */}
        <TabsContent value="subscriptions" className="space-y-6">
          <SubscriptionConfigTab
            config={localSubscriptionConfig}
            onChange={updateSubscriptionConfig}
            disabled={isSaving}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
